import React, { useRef, useState } from "react";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import { uploadSubmissionPhoto } from "@/api/submissions";

/**
 * Foto uploader.
 * onUploaded(url) — geeft de API-url terug (bv /api/uploads/xxx.jpg)
 *
 * existingImage (optioneel): absolute URL van een bestaande foto — bijvoorbeeld
 * bij "profiel aanvullen" waar we de huidige foto tonen zodat de ondernemer weet
 * wat er nu staat. Deze wordt alleen visueel getoond; niet geüpload. Uploadt de
 * gebruiker een nieuwe foto, dan overschrijft die de bestaande in de listing.
 * Doet de gebruiker niets, dan blijft de bestaande foto op het bedrijf staan
 * (backend skipt lege foto_url bij aanvulling).
 */
export const PhotoUpload = ({ value, onUploaded, absoluteUrl, existingImage = "" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(absoluteUrl || "");
  // Toon de bestaande foto wanneer er geen nieuwe upload is en het formulier weet
  // van een huidige foto af.
  const showExisting = !preview && !!existingImage;
  const displayed = preview || (showExisting ? existingImage : "");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Kies een afbeelding (JPG, PNG, WEBP of GIF).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Bestand te groot. Max 5 MB.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const res = await uploadSubmissionPhoto(file);
      setPreview(res.absoluteUrl);
      onUploaded(res.url, res.absoluteUrl);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Uploaden lukte niet.";
      setError(detail);
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    setPreview("");
    onUploaded("", "");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
        Foto {!existingImage && "(optioneel)"}
      </label>
      {displayed ? (
        <div className="relative rounded-xl overflow-hidden border border-[#E4EEF5] bg-[#F4F8FB]">
          {showExisting && (
            <span
              data-testid="photo-current-label"
              className="absolute top-2 left-2 z-10 px-2.5 py-1 rounded-full bg-white/95 shadow-sm text-[10px] font-[Poppins] font-semibold uppercase tracking-wider text-[#173A5E]"
            >
              Huidige foto
            </span>
          )}
          <img
            src={displayed}
            alt="Voorbeeld"
            className="w-full h-52 object-cover"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            {showExisting && (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="photo-replace-btn"
                className="bg-white/95 hover:bg-white rounded-full px-3 py-1.5 shadow-md text-xs font-[Poppins] font-semibold text-[#2E9CD6] inline-flex items-center gap-1"
              >
                <Upload className="w-3.5 h-3.5" />
                Foto vervangen
              </button>
            )}
            {!showExisting && (
              <button
                type="button"
                onClick={clear}
                data-testid="photo-clear"
                className="bg-white/95 hover:bg-white rounded-full p-2 shadow-md text-[#CE1B2A]"
                aria-label="Foto verwijderen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="photo-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full flex flex-col items-center justify-center gap-2 py-8 px-4 border-2 border-dashed border-[#6BC0E8] rounded-xl bg-[#F4F8FB] hover:bg-white hover:border-[#2E9CD6] transition cursor-pointer disabled:cursor-wait"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-[#2E9CD6] animate-spin" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
              <Upload className="w-5 h-5 text-[#2E9CD6]" />
            </div>
          )}
          <span className="text-sm font-[Poppins] font-semibold text-[#173A5E]">
            {uploading ? "Bezig met uploaden…" : "Kies een foto"}
          </span>
          <span className="text-xs text-[#5A7594] font-[Inter]">
            JPG, PNG of WEBP · max 5 MB
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid="photo-file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />
      {error && (
        <p className="mt-2 text-xs font-[Inter] text-[#CE1B2A]">{error}</p>
      )}
      {!error && showExisting && (
        <p className="mt-2 text-xs text-[#5A7594] font-[Inter]">
          Dit is de huidige foto van je bedrijf. Upload een nieuwe om te vervangen — of laat staan om deze te behouden.
        </p>
      )}
      {!error && !preview && !showExisting && (
        <p className="mt-2 text-xs text-[#5A7594] font-[Inter] flex items-center gap-1">
          <ImageIcon className="w-3 h-3" />
          Geen foto? Geen probleem, dan tonen we een nette placeholder.
        </p>
      )}
    </div>
  );
};

export default PhotoUpload;
